import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@myarvato/shared/services';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public static SkipAllInterceptorHandling: string = 'x-skip-all-interceptor-handling';
  public static SkipInterceptorHandling: string = 'x-skip-interceptor-handling';
  public static SkipRateLimitInterceptorHandling: string = 'x-skip-rate-limit-interceptor-handling';
  public static ErrorMessageTargetKey: string = 'x-error-message-target-key';

  /**
   * Intercept error request
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err) => {
        if (req.headers.get(ErrorInterceptor.SkipAllInterceptorHandling)) {
          return throwError(() => err);
        }

        const error = err?.error?.errorCode || 'F0408';
        const traceId = err.headers?.get?.('x-trace-id') || '';

        if (err.status >= 500) {
          this.router.navigate(['error', error, traceId]).then();
          return throwError(() => err);
        }

        if (err.status === 403) {
          this.router.navigate(['error', error]).then();
          return throwError(() => err);
        }

        if (err.status === 429) {
          if (req.headers.get(ErrorInterceptor.SkipRateLimitInterceptorHandling)) {
            return throwError(() => err);
          }
          this.router.navigate(['error', error]).then();
          return throwError(() => err);
        }

        if (req.headers.get(ErrorInterceptor.SkipInterceptorHandling)) {
          return throwError(() => err);
        }

        let errorMessageTargetKey: string | undefined;
        if (req.headers.get(ErrorInterceptor.ErrorMessageTargetKey)) {
          errorMessageTargetKey = req.headers.get(ErrorInterceptor.ErrorMessageTargetKey)!;
        }

        this.notificationService.showError(
          this.translateService.instant('errors.' + error + '.title') || 'Error',
          this.translateService.instant('errors.' + error + '.description') || 'Something went wrong.',
          errorMessageTargetKey,
        );

        return throwError(() => err);
      }),
    );
  }
}
